import AppRoutes from "AppRoutes";
import { createLogger } from "logging";
import { useApi, useLayout, useToasts, useTranslations } from "providers";
import React, { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { AddBoardContext, AddBoardContextProps, AddBoardFieldValues } from "./AddBoardContext";
import { AddBoardPanel } from "./components/AddBoardPanel";

const logger = createLogger("AddBoardProvider");

export const AddBoardProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { boardApi } = useApi();
  const i18n = useTranslations();
  const toasts = useToasts();
  const { handleNavClick } = useLayout();

  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmUnsaved, setConfirmUnsaved] = useState(false);

  const form = useForm<AddBoardFieldValues>({
    defaultValues: { name: "", shortName: "", longName: "" },
  });
  const { control, formState, getValues, reset } = form;
  const { isDirty, isValid } = formState;

  const openAddBoard = useCallback(() => {
    reset({ name: "", shortName: "", longName: "" });
    setIsOpen(true);
  }, [reset]);

  const handleClose = useCallback(
    (ignoreDirty?: boolean) => {
      const confirm = isDirty && !ignoreDirty;
      logger.debug(`handleClose(): isDirty:=${isDirty} (ignore:=${ignoreDirty}) -> ${confirm}`);
      if (confirm) {
        setConfirmUnsaved(true);
      } else {
        reset();
        setIsOpen(false);
      }
    },
    [isDirty, reset]
  );

  const getPatch = useCallback((values: AddBoardFieldValues) => {
    const { name, shortName, longName } = values;
    return { name, shortName, longName };
  }, []);

  const handleSave = useCallback(async () => {
    logger.debug("handleSave():", isValid);
    if (!isValid) return;

    try {
      setIsSubmitting(true);

      const patch = getPatch(getValues());
      const entityTitle = patch.name ?? patch.shortName ?? patch.longName ?? i18n.EntityTitle_Unknown;

      try {
        const result = await boardApi.createBoard(patch);
        toasts.addSuccess(i18n.Board, entityTitle);

        const url = AppRoutes.toBoardSettings(result);
        handleNavClick(url);
        handleClose(true);
      } catch (error) {
        toasts.addFailure(i18n.Board, entityTitle, error);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [isValid, i18n, boardApi, toasts, getPatch, getValues, handleNavClick, handleClose]);

  const contextValue: AddBoardContextProps = useMemo(
    () => ({
      confirmUnsaved,
      setConfirmUnsaved,
      isDirty,
      isValid,
      isSubmitting,
      control,
      form,
      getPatch,
      handleSave,
      handleClose,
      openAddBoard,
    }),
    [confirmUnsaved, isDirty, isValid, isSubmitting, control, form, getPatch, handleSave, handleClose, openAddBoard]
  );

  return (
    <AddBoardContext.Provider value={contextValue}>
      {children}
      {isOpen && <AddBoardPanel />}
    </AddBoardContext.Provider>
  );
};
